'use client';

import { useEffect } from 'react';
import { useUser } from '@clerk/nextjs';
import { Spacing } from '@ghosts-inc/design-system/constants';
import { Close } from '@ghosts-inc/design-system/icons';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { usePathname } from 'next/navigation';
import { AcknowledgeCheckboxLabel } from '../../terms-and-conditions/_components/Footer';
import { TermsAndConditionsContent } from '../../terms-and-conditions/_components/TermsAndConditionsContent';
import { useAcknowledgeTermsAndConditions } from '../../terms-and-conditions/_hook/useAcknowledgeTermsAndConditions';

export const TermsAndConditionsModal = () => {
  const pathname = usePathname();
  // Get clerk session with metadata
  const { user } = useUser();
  const { isAcknowledged, isOpen, isLoading, onOpen, onSubmit, onClose, handleCheckboxChange } =
    useAcknowledgeTermsAndConditions({ user });

  useEffect(() => {
    if (!user) return;

    // The Modal should only be shown once per day for the user.
    // We use localstorage to store the last shown date.
    const lastShownDate = localStorage.getItem('termsAndConditionsLastShownDate');
    // If the last shown date is set, we check if the difference is more than 24 hours.
    // If it is, we show the modal.
    if (lastShownDate) {
      const lastShownDateObj = new Date(lastShownDate);
      const today = new Date();
      const diff = today.getTime() - lastShownDateObj.getTime();

      // If the difference is less than 24 hours, we don't show the modal.
      if (diff < 24 * 60 * 60 * 1000) {
        onClose();
        return;
      }
    }

    onOpen();
    localStorage.setItem('termsAndConditionsLastShownDate', new Date().toISOString());
  }, [user, pathname, onOpen, onClose]);

  return (
    <Dialog
      open={isOpen}
      sx={{
        '& .MuiDialog-paper': {
          p: 0,
          borderRadius: { tablet: 3, mobile: 0 },
          margin: { mobile: 0, tablet: Spacing.xlarge },
          maxHeight: { tablet: 'calc(100vh - 60px)', mobile: 'unset' },
          height: { mobile: '100%', tablet: 'unset' },
        },
      }}
      onClose={onClose}
    >
      <Stack flex={1}>
        <Stack direction="row" justifyContent="flex-end" pt={Spacing.small} pr={Spacing.small}>
          <IconButton aria-label="close" onClick={onClose}>
            <Close sx={{ fontSize: { tablet: 24, mobile: 20 } }} />
          </IconButton>
        </Stack>
        <Stack
          px={{ mobile: Spacing.base, tablet: Spacing.large }}
          pb={{ mobile: Spacing.small, tablet: Spacing.large }}
          pt={{ mobile: Spacing.small, tablet: Spacing.small }}
          flex={1}
        >
          <TermsAndConditionsContent userName={user?.firstName} />
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          borderTop={1}
          borderColor="lightBorder.default"
          bottom={0}
          position="sticky"
          px={Spacing.base}
          py={{ mobile: Spacing.base, tablet: Spacing.large }}
          bgcolor="white"
          gap={Spacing.base}
          flexDirection={{ mobile: 'column', smallTablet: 'row' }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={isAcknowledged}
                data-testid="acknowledge-checkbox"
                name="acknowledge-checkbox"
                onChange={handleCheckboxChange}
              />
            }
            label={<AcknowledgeCheckboxLabel textAlign="left" />}
            sx={{ flex: 1 }}
          />
          <Button
            onClick={onSubmit}
            disabled={!isAcknowledged}
            loading={isLoading}
            sx={{ width: { mobile: '100%', smallTablet: 'auto' } }}
          >
            Acknowledge
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};
